import { LinearProgress } from "@material-ui/core";
import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { useGetExamSessionQuery } from "../../../api/rtk/ExamSessionApi";
import { useGetExamSessionCandidatesQuery } from "../../../api/rtk/ExamSessionCandidateApi";
import { useGetMeAsExpertQuery } from "../../../api/rtk/MeApi";
import { useGetTimeSlotStructureQuery } from "../../../api/rtk/TimeSlotStructureApi";
import { ExamDayViewModel } from "../../../model/ExamDayViewModel";
import { GetLocalizedName } from "../../../model/ExamSessionViewModel";
import { RoomViewModel } from "../../../model/RoomViewModel";
import { TimeSlot, TimeSlotStructure } from "../../../model/TimeSlotStructure";
import { getTimeSlotLabel } from "../../../utils/TimeSlotUtils";
import TranslatedMessage from "../../translations/TranslatedMessage";
import ExpertSchedulerCell from "../expertExams/ExpertSchedulerCell";
import {
  initialSchedulerCandidateContextValue,
  SchedulerCandidate,
  SchedulerCandidateContext,
  SchedulerCandidateContextValue,
  SchedulerExamCandidate
} from "./SchedulerCandidateContext";
import {
  ExamCandidateCaseName,
  initialSchedulerCaseNameContextValue,
  SchedulerCaseNameContext,
  SchedulerCaseNameContextValue
} from "./SchedulerCaseNameContext";
import { initialSchedulerContextValue, SchedulerContext, SchedulerContextValue } from "./SchedulerContext";
import { getCandidatesContextData, getExamDay, getExamTypes } from "./SchedulerContextUtils";
import {
  initialSchedulerExpertContextValue,
  SchedulerExpertContext,
  SchedulerExpertContextValue,
  SchedulerRoomExperts
} from "./SchedulerExpertContext";

export const ExpertSchedulerContextProvider: FC = ({ children }) => {
  const { examSessionId } = useParams<{ examSessionId: string }>();
  const { data: examSession, isFetching: isExamSessionFetching } = useGetExamSessionQuery(examSessionId);
  const { data: examSessionCandidates, isFetching: areExamSessionCandidatesFetching } =
    useGetExamSessionCandidatesQuery(examSessionId);
  const { data: timeSlotStructure, isFetching: isTimeSlotStructureFetching } = useGetTimeSlotStructureQuery();
  const { data: currentExpert, isFetching: isExpertFetching } = useGetMeAsExpertQuery();

  const [selectedExamDayId, setSelectedExamDayId] = useState("");
  const [examDays, setExamDays] = useState<ExamDayViewModel[]>([]);
  const [schedulerCandidates, setSchedulerCandidates] = useState<SchedulerCandidate[]>([]);
  const [schedulerExamCandidates, setSchedulerExamCandidates] = useState<SchedulerExamCandidate[]>([]);
  const [examCandidateCaseNames, setExamCandidateCaseNames] = useState<ExamCandidateCaseName[]>([]);
  const [schedulerExperts, setSchedulerExperts] = useState<SchedulerRoomExperts[]>([]);

  const isSessionPublished = examSession?.published || false;
  const selectedExamDay = useMemo(() => getExamDay(examDays, selectedExamDayId), [examDays, selectedExamDayId]);
  const examTypes = getExamTypes(selectedExamDay);
  const exams = useMemo(
    () => examSession?.exams.filter((exam) => examTypes.includes(exam.type)) || [],
    [examSession, examTypes]
  );

  const rooms = useMemo(() => {
    const allRooms = examSession?.locations.map((location) => location.rooms).flat();
    return selectedExamDay !== undefined && allRooms !== undefined
      ? allRooms.filter((room) =>
          selectedExamDay.roomAssignments.some(
            (roomAssignment) =>
              roomAssignment.roomId === room.id &&
              roomAssignment.assignedUsers.some((user) => user.userId === currentExpert?.id)
          )
        )
      : [];
  }, [examSession, currentExpert, selectedExamDay]);

  const schedulerCaseNameContext = useMemo<SchedulerCaseNameContext>(
    () => ({
      ...initialSchedulerCaseNameContextValue,
      examCandidateCaseNames,
    }),
    [examCandidateCaseNames]
  );

  const examSessionCandidatesContext = useMemo<SchedulerCandidateContext>(
    () => ({
      ...initialSchedulerCandidateContextValue,
      schedulerCandidates,
      schedulerExamCandidates,
    }),
    [schedulerCandidates, schedulerExamCandidates]
  );

  const changeSelectedExamDay = useCallback((examDayId: string) => {
    setSelectedExamDayId(examDayId);
  }, []);

  const schedulerContext = useMemo<SchedulerContext>(() => {
    return {
      ...initialSchedulerContextValue,
      examTypes,
      readonly: true,
      examSessionLabel: examSession !== undefined ? GetLocalizedName(examSession) : "",
      isSessionPublished,
      selectedExamDayId,
      locations: examSession !== undefined ? examSession.locations : [],
      examDays,
      rooms,
      exams,
      timeSlotStructure: timeSlotStructure !== undefined ? timeSlotStructure : ({} as TimeSlotStructure),
      changeSelectedExamDay,
      renderCell: (examIds: string[], room: RoomViewModel, timeSlotIndex: number, key?: string) => (
        <ExpertSchedulerCell examIds={examIds} room={room} timeSlotIndex={timeSlotIndex} key={key} />
      ),
      renderTimeSlotLabel: (timeSlot: TimeSlot) => (
        <>
          <div>{getTimeSlotLabel(timeSlot)}</div>
          <div>
            <TranslatedMessage id="scheduler.timeSlot.arrivalTime" />: {timeSlot.arrivalTime}
          </div>
        </>
      ),
    };
  }, [
    examTypes,
    examSession,
    isSessionPublished,
    selectedExamDayId,
    examDays,
    rooms,
    exams,
    timeSlotStructure,
    changeSelectedExamDay,
  ]);

  const expertSchedulerContext = useMemo<SchedulerExpertContext>(() => {
    return {
      schedulerExperts,
      changeExpert: initialSchedulerExpertContextValue.changeExpert,
    };
  }, [schedulerExperts]);

  useEffect(() => {
    if (examSession === undefined) {
      return;
    }

    const examDays = examSession.examDays
      .filter((examDay) =>
        examDay.roomAssignments.some((assignment) =>
          assignment.assignedUsers.some((user) => user.userId === currentExpert?.id)
        )
      )
      .sort((day1, day2) => new Date(day1.date).getTime() - new Date(day2.date).getTime());
    setExamDays(examDays);
    if (examDays[0] !== undefined) {
      setSelectedExamDayId(examDays[0].id);
    }
  }, [examSession, currentExpert]);

  useEffect(() => {
    if (examSessionCandidates === undefined) {
      return;
    }

    const { schedulerCandidates, schedulerExamCandidates, examCandidateCaseNames } = getCandidatesContextData(
      examSessionCandidates,
      examDays
    );
    setSchedulerCandidates(schedulerCandidates);
    setSchedulerExamCandidates(schedulerExamCandidates);
    setExamCandidateCaseNames(examCandidateCaseNames);
  }, [examSessionCandidates, examDays]);

  useEffect(() => {
    if (examDays === undefined) {
      return;
    }
    setSchedulerExperts(
      examDays.map((ed) => ({
        examDayId: ed.id,
        expertAssignments: ed.roomAssignments.map((ra) => ({
          roomId: ra.roomId,
          assignedUsers: ra.assignedUsers.map((user) => ({ isFixed: user.isFixed, userId: user.userId })),
        })),
      }))
    );
  }, [examDays]);

  if (isExamSessionFetching || areExamSessionCandidatesFetching || isExpertFetching || isTimeSlotStructureFetching) {
    return <LinearProgress variant="query" />;
  }

  return (
    <SchedulerContextValue.Provider value={schedulerContext}>
      <SchedulerCandidateContextValue.Provider value={examSessionCandidatesContext}>
        <SchedulerExpertContextValue.Provider value={expertSchedulerContext}>
          <SchedulerCaseNameContextValue.Provider value={schedulerCaseNameContext}>
            {children}
          </SchedulerCaseNameContextValue.Provider>
        </SchedulerExpertContextValue.Provider>
      </SchedulerCandidateContextValue.Provider>
    </SchedulerContextValue.Provider>
  );
};

export default ExpertSchedulerContextProvider;
